"use client";
import BFLLink from "@repo/shared-ui/BFLLink";
import { FC, useState, ChangeEvent, useEffect } from "react";
import {
  getPageType,
  getUtmParamsFromCookie,
  validateMobileNumber,
} from "@repo/shared-utils/common-utils";
import { INVEST_NOW_MODAL_KEY } from "@repo/shared-utils/shared.constants";

// API function to send OTP
const sendMobileOtp = async (mobileNumber: string) => {
  const { bfslSendOtp } = await import("@repo/sso/bfslLoginApi");
  const utm = getUtmParamsFromCookie();
  const payload = {
    name: "",
    mobile: mobileNumber,
    refferalCode: "",
    utmCampaign: utm?.bflpageType || getPageType("tag-1") || "NA",
  };

  try {
    const bfslSendOtpResponse = await bfslSendOtp(payload);
    return bfslSendOtpResponse;
  } catch (error) {
    console.error("Error sending OTP:", error);
    throw error;
  }
};
interface IOtpSendModal {
  title: string;
  subtitle: string;
  desc: string;
  getOtp: string;
  link: string;
  openInNewTab: string;
  number: string;
  sending: string;
}

export interface DematOtpSendModalProps {
  setOpenModal: Function;
  prefillMobileNumber?: string;
  ssoContent?: IOtpSendModal;
}

const BFLOtpSendModal: FC<DematOtpSendModalProps> = ({
  setOpenModal,
  prefillMobileNumber,
  ssoContent,
}) => {
  const { number = "", link = "", sending = "", getOtp = "", desc = "" } = ssoContent ?? {};
  const [mobileNumber, setMobileNumber] = useState(prefillMobileNumber || "");
  const [mobileError, setMobileError] = useState("");
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [termsError, setTermsError] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  // Update mobile number when prefillMobileNumber prop changes
  useEffect(() => {
    if (prefillMobileNumber) {
      setMobileNumber(prefillMobileNumber);
    }
  }, [prefillMobileNumber]);

  const handleMobileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, "");
    setMobileNumber(value);

    if (mobileError) setMobileError("");
  };
  return (
    <div className="bg-lgray-100 mb-10 px-6 pt-6 pb-8">
      <form>
        <div className="mb-4 flex flex-col">
          <label htmlFor="mobile-input" className="mb-2 text-sm font-medium">
            <span>{number}</span>
            <span className="text-red-600">*</span>
          </label>
          <input
            id="mobile-input"
            type="tel"
            inputMode="numeric"
            maxLength={10}
            placeholder="Enter 10-digit mobile number"
            className={`rounded border ${mobileError ? "border-red-600" : "border-gray-300"} p-3 text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none`}
            value={mobileNumber}
            onChange={handleMobileChange}
          />
          {mobileError && <div className="mt-1 text-xs text-red-600">{mobileError}</div>}
          {validateMobileNumber(mobileNumber).isValid && (
            <div className="mt-1 text-xs text-gray-600">{INVEST_NOW_MODAL_KEY.OtpSend}</div>
          )}
        </div>
        <div className="mb-4">
          <div
            className={`flex items-start gap-2 ${termsError ? "rounded border border-red-600 p-2" : ""}`}
          >
            <input
              type="checkbox"
              className="mt-1"
              checked={termsAccepted}
              onChange={(e) => {
                setTermsAccepted(e.target.checked);
                if (e.target.checked) setTermsError("");
              }}
            />
            <div>
              <p className="text-xs font-medium text-gray-700">
                {INVEST_NOW_MODAL_KEY.termsText}
                <BFLLink href={link} className="text-link-primary ml-1">
                  {INVEST_NOW_MODAL_KEY.termsLink}
                </BFLLink>
              </p>
            </div>
          </div>
          {termsError && <div className="mt-1 text-xs text-red-600">{termsError}</div>}
        </div>
        <div className="flex justify-center">
          <button
            className={`cursor-pointer rounded-[200px] bg-gradient-to-r from-[#ff6700] to-[#ff8900] px-[78px] py-[11px] text-sm leading-[18px] font-medium text-white ${isLoading ? "cursor-not-allowed opacity-50" : ""}`}
            disabled={isLoading}
            onClick={async (e) => {
              e.preventDefault();

              // Reset errors
              setMobileError("");
              setTermsError("");

              let isValid = true;

              // Validate mobile number
              const mobileValidation = validateMobileNumber(mobileNumber);
              if (!mobileValidation.isValid) {
                setMobileError(mobileValidation.errorMessage);
                isValid = false;
              }

              // Validate terms acceptance
              if (!termsAccepted) {
                setTermsError("Please accept our Terms & Conditions");
                isValid = false;
              }

              // Proceed only if all validations pass
              if (isValid) {
                try {
                  setIsLoading(true);

                  // Call send OTP API
                  const otpResponse = await sendMobileOtp(mobileNumber);

                  // Check if OTP was sent successfully
                  if (otpResponse.statusCode === 90 && otpResponse.status === "success") {
                    setOpenModal("verify");
                  } else {
                    setMobileError("Failed to send OTP. Please try again.");
                  }
                } catch (error) {
                  setMobileError("Failed to send OTP. Please try again.");
                  console.error("Error sending OTP:", error);
                } finally {
                  setIsLoading(false);
                }
              }
            }}
          >
            {isLoading ? sending : getOtp}
          </button>
        </div>
        <div className="mt-6 text-center text-[10px] text-black">{desc}</div>
      </form>
    </div>
  );
};

export default BFLOtpSendModal;
